import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { StoreAuxService } from '../../services/storeAux/store-aux.service';
import { environment } from '../../../environments/environment';

import {
  AgencyRebate,
  CatalogListResponse,
  Company,
  CompanyListFilters,
  CompanyRecord,
  CompaniesListResponse,
  CompanyStatusesResponse,
  Contact,
  ContactCreateResponse,
  CountryOption,
  ContactListResponse,
  ExternalIdAvailability,
  FileUploadData,
  FinancesConfig,
  BrandGroupsSearchResponse,
  OwnersSearchResponse,
  OwnerValidationResponse,
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderDetail,
  PurchaseOrderSearchFilters,
  PurchaseOrderSearchResponse,
  PurchaseOrderUpdateRequest,
  CompanyConfigResponse,
  CompanyCreateRequest,
  CompanyDocumentInput,
  CompanyDocumentsResponse,
  CompanyGroup,
  CompanyGroupsResponse,
  ContactCreateRequest,
  ContactUpdateRequest
} from '../models/makers-finances-mgr.models';

@Injectable()
export class MakersFinancesMgrService {
  private readonly BASE_PATH = `${environment.urlGatewayInt}/makers-finances-mgr`;

  readonly config$ = new BehaviorSubject<FinancesConfig | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly storeAux: StoreAuxService
  ) {}

  resolveCountry(country: string): string {
    return country === 'DEV' ? 'CO' : country;
  }

  getConfigs(country: string): Observable<{ message: string; data: FinancesConfig }> {
    const params = new HttpParams({ fromObject: { country: this.resolveCountry(country).toLowerCase() } });
    return this.http.get<{ message: string; data: FinancesConfig }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/configs`),
      { params }
    ).pipe(tap(res => this.config$.next(res.data)));
  }

  // GET /companies/available/purchase-orders — Verified companies available to create Purchase
  // Orders. Used by the create/edit PO form (HU2 Buyer Information) for the "Company to bill" and
  // "Agency" selectors. `email` + `country` travel as headers (_poHeaders); `q` (ilike over name,
  // maker_code, tax_id) and `agency_only` (type_of_company = 'Agency') travel as query params.
  // The `country` arg is kept for signature compatibility with the previous /companies/search call.
  searchCompanies(
    country: string,
    q?: string,
    options: { agencyOnly?: boolean } = {}
  ): Observable<{ message: string; data: Company[] }> {
    const fromObject: Record<string, string> = {};
    if (q) {
      fromObject['q'] = q;
    }
    if (options.agencyOnly !== undefined) {
      fromObject['agency_only'] = String(options.agencyOnly);
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/available/purchase-orders`),
      { params, headers: this._poHeaders(), responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Company[] }>(text)));
  }

  // POST /makers-finances-mgr/companies/search — paginated company list for the Companies tab.
  // `page`/`limit` travel in the query string; filters (q, statuses, types, owners) travel in the
  // body — only present, non-null fields are sent. The email + country headers come from _poHeaders().
  // Records are normalized (status/type → snake_case codes) so the component can use them directly
  // with COMPANY_STATUS / COMPANY_TYPE constants and the SCSS badge classes.
  getCompanies(
    filters: CompanyListFilters,
    page = 1,
    limit = 10,
  ): Observable<CompaniesListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    const body = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null),
    );
    return this.http.post<CompaniesListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/search`),
      body,
      { params, headers: this._poHeaders().set('Content-Type', 'application/json') },
    ).pipe(
      map(res => ({
        ...res,
        data: {
          ...res.data,
          records: res.data.records.map(r => this._normalizeRecord(r)),
        },
      })),
    );
  }

  // GET /companies/:id — company detail for the Companies tab. Parsed as text + _parseSafeJson so
  // the 19-digit legacy_zoho_id is preserved as a string (same as getPurchaseOrder). The record
  // is normalized (status/type → snake_case) so the view can use COMPANY_STATUS / COMPANY_TYPE.
  getCompany(id: number): Observable<{ message: string; data: CompanyRecord }> {
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}`),
      { headers: this._poHeaders(), responseType: 'text' },
    ).pipe(map(text => {
      const res = this._parseSafeJson<{ message: string; data: CompanyRecord }>(text);
      return { ...res, data: this._normalizeRecord(res.data) };
    }));
  }

  searchContacts(companyId: string, q?: string): Observable<{ message: string; data: Contact[] }> {
    let fromObject: Record<string, string> = { company_id: companyId, country: this.resolveCountry(this.storeAux.country) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts/search`),
      { params, responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Contact[] }>(text)));
  }

  // GET /contacts?page=&limit=&q= — confirmed against internal/infra/api/handler/contact/list.go
  // and internal/app/contact/list.go in makers-finances-mgr: a paginated, BBR-native contact
  // directory with no company_id requirement (distinct from /contacts/search above, which is
  // Zoho-backed and company-scoped for the PO form's "Point of contact"). Backs Legal POC /
  // Financial POC so those selects work before a company has been saved.
  listContacts(page = 1, limit = 10, q?: string): Observable<ContactListResponse> {
    let fromObject: Record<string, string> = { page: String(page), limit: String(limit) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<ContactListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts`),
      { params, headers: this._emailHeaders() }
    );
  }

  previewFile(key: string, country: string): Observable<Blob> {
    const params = new HttpParams({ fromObject: { key, country } });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/preview`),
      { params, responseType: 'blob' }
    );
  }

  uploadFile(file: File, country: string): Observable<{ message: string; data: FileUploadData }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('country', this.resolveCountry(country));
    return this.http.post<{ message: string; data: FileUploadData }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/upload`),
      formData
    );
  }

  searchOwners(country: string, page = 1, limit = 10, q?: string): Observable<OwnersSearchResponse> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country), page: String(page), limit: String(limit) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<OwnersSearchResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/owners/search`),
      { params, headers: this._poHeaders() }
    );
  }

  // GET /brand-groups?country=&name=&page= — proxy to CMS store-config, populates "Brand Group to
  // execute" when business_unit = retailer_ads. `name` is a substring filter (case-insensitive) —
  // there is no filter by id. Page size is fixed at 30 server-side (not configurable from here).
  // Only the `email` header is required (unlike most PO endpoints, `country` travels as a query
  // param here, not a header).
  listBrandGroups(country: string, name?: string, page = 1): Observable<BrandGroupsSearchResponse> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country), page: String(page) };
    if (name) {
      fromObject = { ...fromObject, name };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<BrandGroupsSearchResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/brand-groups`),
      { params, headers: this._emailHeaders() },
    );
  }

  // GET /owners/validate?email=... — validates that the owner email exists in Zoho.
  validateOwner(email: string): Observable<OwnerValidationResponse> {
    const params = new HttpParams({ fromObject: { email } });
    return this.http.get<OwnerValidationResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/owners/validate`),
      { params, headers: this._poHeaders() },
    );
  }

  // HU3 — POST /purchase-orders/search?page=&limit=
  // page/limit travel in the query string (the backend ignores them in the body via json:"-").
  // Filters travel in the body; only present fields are sent.
  searchPurchaseOrders(
    filters: PurchaseOrderSearchFilters,
    page = 1,
    limit = 10,
  ): Observable<{ message: string; data: PurchaseOrderSearchResponse }> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    const body = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null),
    );
    return this.http.post<{ message: string; data: PurchaseOrderSearchResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/search`),
      this._stringifyBody(body),
      { params, headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // HU3 — GET /purchase-orders/:id
  // Parsed as text + _parseSafeJson so the 19-digit Zoho company_to_bill_id is preserved as a
  // string instead of being truncated by JSON.parse (same pattern as searchCompanies).
  getPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrderDetail }> {
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      { headers: this._poHeaders(), responseType: 'text' },
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: PurchaseOrderDetail }>(text)));
  }

  // PR #38: on-blur validation against GET /purchase-orders/external-id/availability.
  // companyToBillId is the Zoho int64 held as a string (see _stringifyBody) to avoid float
  // precision loss. excludeId is optional (only used when editing an existing PO).
  checkExternalIdAvailability(
    externalId: string,
    companyToBillId: string,
    country: string,
    excludeId?: number,
  ): Observable<{ message: string; data: ExternalIdAvailability }> {
    let fromObject: Record<string, string> = {
      external_id: externalId,
      company_to_bill_id: companyToBillId,
      country: this.resolveCountry(country),
    };
    if (excludeId != null) {
      fromObject = { ...fromObject, exclude_id: String(excludeId) };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<{ message: string; data: ExternalIdAvailability }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/external-id/availability`),
      { params, headers: this._emailHeaders() },
    );
  }

  createPurchaseOrder(body: PurchaseOrderCreateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  updatePurchaseOrder(id: number, body: PurchaseOrderUpdateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.put<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  submitPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/submit`),
      null,
      { headers: this._poHeaders() }
    );
  }

  // POST /purchase-orders/:id/cancel — transitions a pre-approval PO to `canceled`.
  // `reason` is optional (reserved for audit). Returns the full PO representation.
  cancelPurchaseOrder(id: number, reason?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = reason != null && reason !== '' ? { reason } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/cancel`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/close — transitions an approved PO to `closed`.
  closePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/close`),
      {},
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/reopen — transitions a closed PO back to `approved`.
  reopenPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/reopen`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/approve — approves a PO (client or paid_by_rappi).
  approvePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/approve`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/return-to-kam — returns a PO to the KAM with a required comment.
  returnToKam(id: number, comment: string): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/return-to-kam`),
      { comment },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/resubmit — revalidates and resubmits a PO returned to KAM.
  // `comment` is optional (max 2000 chars).
  resubmitPurchaseOrder(id: number, comment?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = comment != null && comment !== '' ? { comment } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/resubmit`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/retry-settlement — retries settlement registration.
  // Note: response has no `data` field, only `message`.
  retrySettlement(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/retry-settlement`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/reset-trato — retries Trato contract creation.
  retryTrato(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/reset-trato`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/comments — adds a comment to a PO (no stage transition).
  // Response has no `data`, only `message`.
  addComment(id: number, body: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/comments`),
      { body },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /mdm-core/{country}/makers/search — MDM maker search for filter dropdowns.
  searchMakers(page: number, size: number, search: string): Observable<{ content: { id: number; code: string; name: string }[] }> {
    const url = `${this.storeAux.getCountry(environment.urlMdmCore)}/makers/search`;
    const params = new HttpParams({ fromObject: {
      page: String(page),
      size: String(size),
      direction: 'DESC',
      orderBy: 'createdAt',
    }});
    return this.http.post<{ content: { id: number; code: string; name: string }[] }>(
      url,
      search ? { name: search } : {},
      { params },
    );
  }

  getSourceTypes(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/source-types`),
      { params, headers: this._emailHeaders() }
    );
  }

  // GET /company-type-options — catalog of company types for the Companies tab filter.
  // Same shape as getSourceTypes (CatalogListResponse with records + pagination).
  getCompanyTypeOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/company-type-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  // GET /countries?lang= — full country catalog ({code, name}[]). Unlike the other catalog
  // endpoints it returns a flat array with no pagination, so callers page/filter client-side.
  getCountries(lang = 'en'): Observable<CountryOption[]> {
    const params = new HttpParams({ fromObject: { lang } });
    return this.http.get<CountryOption[]>(
      this.storeAux.getCountry(`${this.BASE_PATH}/countries`),
      { params, headers: this._emailHeaders() }
    );
  }

  // GET /companies/statuses — catalog of company statuses for the Companies tab Status filter.
  // Requires the `country` header (resolved DEV → CO via _poHeaders). Unlike the other catalog
  // endpoints, this one returns a flat array (no pagination wrapper), and the backend's UPPER_CASE
  // codes are normalized to snake_case so they match COMPANY_STATUS and can be sent as the
  // `statuses` filter.
  getCompanyStatuses(): Observable<CompanyStatusesResponse> {
    return this.http.get<CompanyStatusesResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/statuses`),
      { headers: this._poHeaders() },
    ).pipe(
      map(res => ({
        ...res,
        data: res.data.map(s => ({ ...s, code: this._normalizeStatusCode(s.code) })),
      })),
    );
  }

  // Normalizes a backend status code (UPPER_CASE, possibly with spaces) to the snake_case code
  // used by COMPANY_STATUS — lowercase + spaces → "_". Mirrors the status normalization in
  // _normalizeRecord so filter codes match the codes returned by /companies/list.
  private _normalizeStatusCode(code: string): string {
    return code ? code.toLowerCase().replace(/\s+/g, '_') : code;
  }

  getTagOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/tag-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  getPoTypeOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/po-type-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  // ─── Company creation form ───────────────────────────────────────────────────────────────────
  // The endpoints below back the config-driven "New company" form. All routes, verbs and payload
  // shapes have been confirmed against docs/swagger.json in the makers-finances-mgr repo
  // (github.com/rappi-inc/makers-finances-mgr) — see docs/backend-endpoints-companies-form.md for
  // the full reconciliation.

  // GET /companies/config/{country}?is_international= — confirmed (operationId company-config-get).
  // Returns the per-country field configuration (type / required / visible / options / regex) that
  // the form merges with companies-form.config.json. Refetched whenever `international_company`
  // changes, because the fiscal field set differs for international companies.
  getCompanyFieldConfig(country: string, isInternational = false): Observable<CompanyConfigResponse> {
    const params = new HttpParams({ fromObject: { is_international: String(isInternational) } });
    return this.http.get<CompanyConfigResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/config/${this.resolveCountry(country).toLowerCase()}`),
      { params, headers: this._poHeaders() }
    );
  }

  // GET /company-groups?page=&limit= — confirmed (operationId company-group-list). The backend
  // does not accept a `q` filter on this endpoint (only page/limit + the email header); `q` is
  // still sent for forward compatibility but the backend currently ignores it, so the
  // "Company group" select's search box does not actually filter server-side yet.
  getCompanyGroups(page = 1, limit = 10, q?: string): Observable<CompanyGroupsResponse> {
    let fromObject: Record<string, string> = { page: String(page), limit: String(limit) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<CompanyGroupsResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/company-groups`),
      { params, headers: this._poHeaders() }
    );
  }

  // POST /company-groups — confirmed (operationId company-group-create, body: {name}).
  // Backs the "Can't find a group? Create one" affordance under the Company group select.
  createCompanyGroup(name: string): Observable<{ message: string; data: CompanyGroup }> {
    return this.http.post<{ message: string; data: CompanyGroup }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/company-groups`),
      { name },
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  // GET /company-groups/{id} — confirmed (operationId company-group-get, requires the `email`
  // header via RequireAuthenticatedUser). Resolves a saved company_group_id back into its name
  // when the company form loads in edit/draft mode, since the Company group select only caches
  // options from the pages it has fetched.
  getCompanyGroup(id: number): Observable<{ message: string; data: CompanyGroup }> {
    return this.http.get<{ message: string; data: CompanyGroup }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/company-groups/${id}`),
      { headers: this._poHeaders() }
    );
  }

  // POST /contacts — confirmed against docs/swagger.json (dto/contact.CreateRequest /
  // GetResponse) in makers-finances-mgr. Backs the "Create new contact" affordance under the
  // Legal POC / Financial POC selects. Only `email` header is required by this endpoint (no
  // `country`), but _poHeaders() sends both, which the backend simply ignores.
  createContact(body: ContactCreateRequest): Observable<{ message: string; data: ContactCreateResponse }> {
    return this.http.post<{ message: string; data: ContactCreateResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  // GET /contacts/{id} — confirmed (operationId contact-get, dto/contact.GetResponse; no
  // RequireAuthenticatedUser on this route, unlike /company-groups/{id}). Resolves a saved
  // legal_poc_id/financial_poc_id back into its full name + email when the company form loads in
  // edit/draft mode, for the same reason as getCompanyGroup above.
  getContact(id: number): Observable<{ message: string; data: ContactCreateResponse }> {
    return this.http.get<{ message: string; data: ContactCreateResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts/${id}`),
      { headers: this._emailHeaders() }
    );
  }

  // PUT /contacts/{id} — updates an existing contact. Email travels in the request header
  // (same as other BBR-native endpoints that use _emailHeaders). Returns the updated contact.
  updateContact(id: number, body: ContactUpdateRequest): Observable<{ message: string; data: ContactCreateResponse }> {
    return this.http.put<{ message: string; data: ContactCreateResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts/${id}`),
      body,
      { headers: this._emailHeaders().set('Content-Type', 'application/json') }
    );
  }

  // POST /companies — confirmed (operationId company-create-post, dto/company.CreateRequest).
  // Always lands in Draft status (the backend hardcodes it); the form calls submitCompany
  // separately for the explicit "Submit for approval" action.
  createCompany(body: CompanyCreateRequest): Observable<{ message: string; data: CompanyRecord }> {
    return this.http.post(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json'), responseType: 'text' }
    ).pipe(map(text => {
      const res = this._parseSafeJson<{ message: string; data: CompanyRecord }>(text);
      return { ...res, data: this._normalizeRecord(res.data) };
    }));
  }

  // PUT /companies/{id} — confirmed (operationId company-update-put).
  updateCompany(id: number, body: CompanyCreateRequest): Observable<{ message: string; data: CompanyRecord }> {
    return this.http.put(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json'), responseType: 'text' }
    ).pipe(map(text => {
      const res = this._parseSafeJson<{ message: string; data: CompanyRecord }>(text);
      return { ...res, data: this._normalizeRecord(res.data) };
    }));
  }

  // POST /companies/{id}/submit-for-verification — confirmed against docs/swagger.json
  // (operationId company-submit-for-verification). Not `/submit`: that route does not exist.
  // The response has no `data`, only `message` — it doesn't return the updated company.
  submitCompany(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/submit-for-verification`),
      null,
      { headers: this._poHeaders() }
    );
  }

  // GET /companies/{id}/agency-rebates — confirmed against docs/swagger.json
  // (operationId agency-rebate-list-by-company-get). Used to seed the rebates card when a company
  // that already has saved rows is opened in edit mode. Saving no longer round-trips through this
  // endpoint's per-row siblings — PUT /companies/{id} now reconciles the whole `agency_rebate`
  // array inline (see docs/PRD/agency-rebates-put-company.md and company-form.payload.ts).
  listAgencyRebates(companyId: number): Observable<{ message: string; data: AgencyRebate[] }> {
    return this.http.get<{ message: string; data: AgencyRebate[] }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${companyId}/agency-rebates`),
      { headers: this._poHeaders() },
    );
  }

  // Multi-file upload for the Tax document / Contract cards. There is no batch endpoint, so this
  // fans out over the existing single-file POST /files/upload. Note there is no rollback: a
  // partial failure leaves the successful keys uploaded, which the card surfaces per file.
  uploadFiles(files: File[], country: string): Observable<FileUploadData[]> {
    if (!files.length) {
      return of([]);
    }
    return forkJoin(files.map(file => this.uploadFile(file, country).pipe(map(res => res.data))));
  }

  // ─── Company documents (existing company) ───────────────────────────────────────────────────
  // Confirmed against docs/documents/COMPANY_DOCUMENTS_API.md in makers-finances-mgr. Only used
  // once a company exists: while still creating one, uploaded files travel inline in the
  // `documents` array of POST /companies instead (see company-form.payload.ts).

  // GET /companies/:id/documents — every non-deleted document for the company, across all
  // field_names. The company-files-card filters this by its own field's `payload` to find the
  // documents it owns.
  getCompanyDocuments(companyId: number): Observable<CompanyDocumentsResponse> {
    return this.http.get<CompanyDocumentsResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${companyId}/documents`),
      { headers: this._poHeaders() },
    );
  }

  // POST /companies/:id/documents — associates already-uploaded file-manager keys to an existing
  // company. The response carries no `data`, only `message`: the caller must re-fetch
  // getCompanyDocuments afterwards to learn the ids assigned to the new rows.
  addCompanyDocuments(companyId: number, documents: CompanyDocumentInput[]): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${companyId}/documents`),
      { documents },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // DELETE /companies/:id/documents/:docId — soft-delete (204 No Content). Rejected with 400 when
  // it would drop the field_name's count below its configured CMS minimum.
  deleteCompanyDocument(companyId: number, docId: number): Observable<void> {
    return this.http.delete<void>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${companyId}/documents/${docId}`),
      { headers: this._poHeaders() },
    );
  }

  private _emailHeaders(): HttpHeaders {
    return new HttpHeaders({ email: localStorage.getItem('email') || '' });
  }

  // Normalizes backend type_of_company values (e.g. "Maker", "Service Provider") to the snake_case
  // codes used by COMPANY_TYPE. Multi-word variants are mapped explicitly; single-word values are
  // lowercased. Unknown values fall back to the lowercased input so new backend types surface
  // visibly (the type column renders blank via TYPE_LABELS[c.type] until a mapping is added).
  private static readonly TYPE_NORMALIZATION: Readonly<Record<string, string>> = {
    'maker':            'maker',
    'retailer':         'retailer',
    'maker & retailer': 'maker_retailer',
    'agency':           'agency',
    'service provider': 'service_provider',
    'service provider/partnerships': 'service_provider',
  };

  private _normalizeRecord(record: CompanyRecord): CompanyRecord {
    return {
      ...record,
      // Backend statuses use UPPER_CASE with spaces (e.g. "AWAITING FINANCIAL VALIDATION");
      // normalize to the snake_case codes used by COMPANY_STATUS (lowercase + spaces → "_").
      status: record.status ? record.status.toLowerCase().replace(/\s+/g, '_') : record.status,
      type_of_company: this._normalizeType(record.type_of_company),
      type_of_company_raw: record.type_of_company,
    };
  }

  private _normalizeType(type: string): string {
    if (!type) {
      return type;
    }
    const key = type.toLowerCase();
    return MakersFinancesMgrService.TYPE_NORMALIZATION[key] ?? key;
  }

  // PR #56: all Purchase Order endpoints require a `country` header (ISO alpha-2).
  // resolveCountry maps DEV → CO.
  private _poHeaders(): HttpHeaders {
    return this._emailHeaders().set('country', this.resolveCountry(this.storeAux.country));
  }

  // Quotes integers with 16+ digits before JSON.parse to prevent float precision loss.
  private _parseSafeJson<T>(text: string): T {
    return JSON.parse(text.replace(/(:\s*)(\d{16,})/g, '$1"$2"'));
  }

  // Zoho IDs (company_to_bill_id, contact_id, agency_id) are 19-digit int64 values that exceed
  // Number.MAX_SAFE_INTEGER. JSON.stringify can't emit them at full precision: a JS number gets
  // truncated, and a string gets quoted (which Go's *int64 rejects with 400). So we stringify
  // with the IDs as strings, then unquote just these numeric fields to emit raw numeric literals
  // that Go unmarshals to int64 at full precision. Only all-digit values are unquoted; a
  // malformed value stays quoted and surfaces as a visible 400 instead of a silent truncation.
  // Also covers the company-form's *int64 id fields (dto/company.CreateRequest): fh-select always
  // yields a string id (see FhSelectComponent/serializeField), but Go's json.Unmarshal rejects a
  // quoted string for an int64/*int64 field regardless of digit count — not just the 19-digit
  // Zoho ids this list originally targeted for precision.
  private static readonly INT64_FIELDS: ReadonlyArray<string> = [
    'company_to_bill_id', 'contact_id', 'agency_id',
    'company_group_id', 'legal_poc_id', 'financial_poc_id',
    'city_id', 'state_id', 'province_id', 'commune_id',
  ];

  // Int64 IDs that travel as string[] — each element is unquoted so Go unmarshals []int64 at full precision.
  private static readonly INT64_ARRAY_FIELDS: ReadonlyArray<string> = [
    'company_ids',
    'brand_group_ids',
  ];

  private _stringifyBody(body: object): string {
    let json = JSON.stringify(body);
    for (const field of MakersFinancesMgrService.INT64_FIELDS) {
      json = json.replace(new RegExp(`"${field}"\\s*:\\s*"(\\d+)"`, 'g'), `"${field}":$1`);
    }
    for (const field of MakersFinancesMgrService.INT64_ARRAY_FIELDS) {
      json = json.replace(new RegExp(`"${field}"\\s*:\\s*\\[([^\\]]*)\\]`, 'g'),
        (match, content: string) => `"${field}":[${content.replace(/"(\d+)"/g, '$1')}]`);
    }
    return json;
  }
}
